import { UpperCasePipe } from '@angular/common';
import { computed, inject, InputSignal, OnInit, Signal, Component, input } from '@angular/core';

import { take } from 'rxjs';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { injectDevFlagSignal } from '@core/tellent-dev-flags-logic';
import { TellentCurrentUserDataFacade, TellentMembersDataFacade } from '@core/tellent-logic';
import {
  SeatKind,
  TeamMemberStatus,
  TellentDevFlag,
  type TellentMember,
} from '@core/tellent-types';
import { resolveMemberFullName, TellentComponentsNotificationService } from '@core/tellent-util';
import {
  AvatarComponent,
  BadgeComponent,
  ConnectedOverlayDirective,
  DialogRef,
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
} from '@core/user-interface-legacy';

import { EditMemberStatusComponent } from './edit-member-status/edit-member-status.component';
import { EditMemberStatusActionsComponent } from './edit-member-status-actions/edit-member-status-actions.component';
import { EditMembersActionsService } from './edit-members-actions.service';

@Component({
  selector: 'tlc-edit-member-header',
  imports: [
    AvatarComponent,
    BadgeComponent,
    TranslatePipe,
    UpperCasePipe,
    IconButtonComponent,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    DropdownComponent,
    DropdownItemComponent,
    HeadingComponent,
    IconComponent,
    EditMemberStatusComponent,
    EditMemberStatusActionsComponent,
  ],
  templateUrl: './edit-member-header.component.html',
  styleUrl: './edit-member-header.component.less',
})
export class EditMemberHeaderComponent implements OnInit {
  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly i18n = inject(I18nService);
  private readonly notificationsService = inject(TellentComponentsNotificationService);
  private readonly dialogRef = inject(DialogRef);
  private readonly editMembersService = inject(EditMembersActionsService);

  protected readonly DropdownWidth = DropdownWidth;
  protected readonly TeamMemberStatus = TeamMemberStatus;

  public avatarThumbUrl: InputSignal<string> = input.required();
  public isOwner: InputSignal<boolean> = input.required();
  public isDeactivated: InputSignal<boolean> = input.required();
  public email: InputSignal<string> = input.required();
  public memberGuid: InputSignal<string> = input.required();

  protected memberData: Signal<TellentMember | null>;
  protected me = this.currentUserFacade.currentUser;

  protected usersWithoutEmails = injectDevFlagSignal(TellentDevFlag.UsersWithoutEmails);

  protected hasWorkEmail = computed(() => {
    if (!this.usersWithoutEmails()) {
      return true; // Legacy behavior
    }
    return !!this.memberData()?.hasWorkEmail;
  });

  protected hasTemporaryPassword = computed(() => {
    if (!this.usersWithoutEmails()) {
      return false; // Legacy behavior
    }
    return !!this.memberData()?.hasTemporaryPassword;
  });

  protected loginMethod = computed(() => {
    if (!this.usersWithoutEmails()) {
      return 'email'; // Legacy behavior
    }
    if (this.memberData()?.login && !this.hasWorkEmail()) {
      return 'username';
    }
    return 'email';
  });

  protected hasKiwiSeat = computed(() => {
    const seats = this.memberData()?.seats || [];
    return seats.some(seat => seat.app.kind === SeatKind.CoreHR);
  });

  protected showDeactivate = computed(
    () => !(this.me()?.guid === this.memberGuid() || this.memberData()?.owner),
  );
  protected fullName = computed(() => resolveMemberFullName(this.memberData()!));
  protected memberStatus = computed(() => this.memberData()?.status!);

  protected hasDropdownItemsAboveDeactivate = computed(() => {
    const status = this.memberStatus();
    const login = this.loginMethod();
    const hasTemp = this.hasTemporaryPassword();
    const owner = this.memberData()?.owner;
    const hasPersonalEmail = !!this.memberData()?.personalEmail;
    const workEmail = this.hasWorkEmail();
    const kiwiSeat = this.hasKiwiSeat();
    const featureFlag = this.usersWithoutEmails();

    const hasInviteOrPasswordReset = status === TeamMemberStatus.NotInvited || owner;
    const hasResetOrChangeLogin = featureFlag && (login === 'username' || hasTemp);
    const hasPasswordResetEmail = login === 'email' && status === TeamMemberStatus.Active;
    const hasStartPreonboarding =
      featureFlag &&
      status === TeamMemberStatus.NotInvited &&
      (!hasPersonalEmail || (hasPersonalEmail && !workEmail));
    const hasAddEmployeeAction = featureFlag && status === TeamMemberStatus.NotInvited && !kiwiSeat;

    return (
      hasInviteOrPasswordReset ||
      hasResetOrChangeLogin ||
      hasPasswordResetEmail ||
      hasStartPreonboarding ||
      hasAddEmployeeAction
    );
  });

  public ngOnInit() {
    this.memberData = this.membersDataFacade.teamMemberById(this.memberGuid());
  }

  protected toggleActionsMenu(dropdown: ConnectedOverlayDirective): void {
    dropdown.open = !dropdown.open;
  }

  protected async deleteUserClicked() {
    await this.editMembersService.deleteMember(this.memberGuid(), this.fullName());
    this.membersDataFacade.deleteMemberSuccess$.pipe(take(1)).subscribe(() => {
      this.notificationsService.success(
        this.i18n.translate('tlc.members.edit_member.delete_dialog.success_toast', {
          member: this.fullName(),
        }),
      );
      this.dialogRef.close();
    });
  }

  protected actionInvite(): void {
    this.editMembersService.sendInvitation(this.memberGuid());
  }

  protected actionSendPasswordResetEmail(): void {
    this.editMembersService.sendPasswordResetEmail(this.memberData()!.userGuid);
  }

  protected async deactivateUserClicked(): Promise<void> {
    await this.editMembersService.deactivateMember(this.memberData()!.guid, this.fullName());
  }

  protected changeLoginMethod() {
    this.editMembersService.changeLoginMethod(this.memberData()!.login!, this.memberGuid());
  }

  protected resetPassword(): void {
    this.editMembersService.resetPassword(this.memberData()!, this.fullName(), this.dialogRef);
  }

  protected actionStartPreonboarding(): void {
    this.editMembersService.startPreonboarding(this.memberGuid());
  }

  protected actionAddEmployeeToTheListWithoutAccess(): void {
    this.editMembersService.addEmployeeToListWithoutAccess();
  }

  protected onStatusActionClicked(action: 'preonboarding' | TeamMemberStatus) {
    if (action === 'preonboarding') {
      this.editMembersService.startPreonboarding(this.memberGuid());
      return;
    }

    switch (action) {
      case TeamMemberStatus.NotInvited:
      case TeamMemberStatus.Invited:
        this.editMembersService.sendInvitation(this.memberGuid());
        break;
      case TeamMemberStatus.Active:
        this.editMembersService.sendPasswordResetEmail(this.memberData()!.userGuid);
        break;
      case TeamMemberStatus.Deactivated:
        this.editMembersService.reactivateMember(
          this.memberGuid(),
          this.fullName(),
          this.memberData()!.seats,
        );
        break;
      default:
        break;
    }
  }
}
